import { useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, TextField } from '@mui/material'
import { Section } from 'components/controls/section'
import { validationMessages } from 'constants/validation'

import { RegisterFormData, ValidateResult } from 'interface/user/register'
import { AgreementResult } from 'interface/agreement'

const {
  Required,
  TooLongLength,
  TooShortLength,
  NotMuchPassword
} = validationMessages

const formSchema = yup.object().shape({
  userName: yup.string()
    .required(Required)
    .min(1, TooShortLength(1))
    .max(20, TooLongLength(20)),
  password: yup
    .string()
    .required(Required)
    .min(8, TooShortLength(8)),
  passwordConfirm: yup
    .string()
    .required(Required)
    .oneOf([yup.ref('password')], NotMuchPassword),
})
type FormType = yup.InferType<typeof formSchema>

export const useRegisterForm = (
  domain: string,
  validateResult: ValidateResult | null,
  agreement: AgreementResult | null
) => {
  const form = useForm({
    defaultValues: {
      userName: '',
      password: '',
      passwordConfirm: ''
    },
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(formSchema)
  })
  const submit = useCallback(
		(input: FormType, inviteToken: string, agreementVersion?: number): RegisterFormData => ({
      inviteToken,
      userName: input.userName,
      password: input.password,
      agreementVersion: agreementVersion ?? null
    }),
    []
  )
  return {
    domain,
    validateResult,
    agreement,
    form,
    submit
  }
}

export const UserRegisterForm = ({
  validateResult,
  form: {
    control,
    setValue,
    formState: { errors },
  },
}: ReturnType<typeof useRegisterForm>) => {
  useEffect(
    () => {
      if (validateResult && validateResult.defaultName) {
        setValue('userName', validateResult.defaultName)
      }
    },
    [validateResult, setValue]
  )
  return (
    <Stack direction="column">
      <Section label="ユーザー名" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="userName"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="ユーザー名"
              size="small"
              error={!!errors.userName}
              helperText={errors.userName ? errors.userName.message : ''}
            />
          )}
        />
      </Section>

      <Section label="パスワード" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="パスワード"
              autoComplete="new-password"
              type="password"
              size="small"
              error={!!errors.password}
              helperText={errors.password ? errors.password.message : ''}
            />
          )}
        />
      </Section>

      <Section label="確認用パスワード" labelProps={{ paddingY: 2 }}>
        <Controller
          control={control}
          name="passwordConfirm"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="確認用パスワード"
              autoComplete="new-password-confirm"
              type="password"
              size="small"
              error={!!errors.passwordConfirm}
              helperText={
                errors.passwordConfirm ? errors.passwordConfirm.message : ''
              }
            />
          )}
        />
      </Section>
    </Stack>
  )
}
